import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";
import type { Channel } from "../chatTypes";

/**
 * Admin actions on a channel from the sidebar: move it into a folder (or out of one), rename it (asks for the new
 * name in the in-app prompt) and delete it (after a confirmation). Each one tells the server and then asks for a
 * fresh channel list.
 */
export function useChannelAdminActions({ socket, currentVoidId, channels, askInput }: {
  socket: { emit: (event: string, payload?: unknown) => unknown };
  currentVoidId: string;
  channels: Channel[];
  askInput: (cfg: { title?: string; label?: string; initialValue?: string; okText?: string }) => Promise<string | null>;
}) {
  async function moveChannelToFolder(channelId: string, folderId: string | null) {
    if (!currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      // Build payload preserving existing positions; put moved channel at top of target group
      const grouped = new Map<string, Channel[]>();
      for (const ch of channels) {
        const fid = ch.folderId || '';
        if (!grouped.has(fid)) grouped.set(fid, []);
        grouped.get(fid)!.push(ch);
      }
      const targetKey = folderId || '';
      const targetList = grouped.get(targetKey) || [];
      const nextPosition = (targetList.reduce((max, ch) => Math.max(max, Number(ch.position || 0)), 0) || 0) + 10;
      const payload = channels.map((c, idx) => ({
        channelId: c.id,
        folderId: c.id === channelId ? folderId : (c.folderId || null),
        position: c.id === channelId ? nextPosition : Number(c.position || ((channels.length - idx) * 10)),
      }));
      await api.patchAuth(`/spaces/${encodeURIComponent(currentVoidId)}/channels/reorder`, { channels: payload }, tok);
      // Refresh channels
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to move channel', 'error');
    }
  }

  async function renameChannelInline(ch: Channel) {
    // Use the in-app modal, not window.prompt — the desktop (.exe) renderer doesn't
    // implement prompt() (it returns null), so the native prompt silently no-ops there.
    const nm = (await askInput({ title: 'Rename channel', label: 'Channel name', initialValue: ch.name, okText: 'Rename' }))?.trim();
    if (!nm || !currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      await api.postAuth('/channels/rename', { spaceId: currentVoidId, channelId: ch.id, name: nm }, tok);
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to rename channel', 'error');
    }
  }

  async function deleteChannelInline(ch: Channel) {
    const ok = confirm(`Delete channel #${ch.name}?`);
    if (!ok || !currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      await api.postAuth('/channels/delete', { spaceId: currentVoidId, channelId: ch.id }, tok);
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to delete channel', 'error');
    }
  }
  return { moveChannelToFolder, renameChannelInline, deleteChannelInline };
}
